import { Card, Group, Skeleton, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import {
  IconBan,
  IconCircleCheck,
  IconCircleDot,
  IconHourglassEmpty,
  IconLock,
  IconLogout,
  IconPlayerPause,
  IconPlayerPlay,
  IconSparkles,
  IconUserCheck,
} from '@tabler/icons-react';
import { brandSolidStyle, brandTint } from '@/utils/color';

/**
 * StatusKPICard — the clickable status-count tile used above status-filtered lists.
 *
 * Lifted out of `Counselling/Cases/Components/CasesListView.tsx` so the admin Overview,
 * the personal Dashboard and the case list all render the same tile from one definition.
 *
 * Counts are supplied by the caller — each page decides whether they are global
 * (`listView({ take: 1, statusID })` per status) or scoped to the current user.
 */

/**
 * Maps a lookup's free-text `icon` string to a Tabler icon.
 * Unrecognised values fall back to a generic dot.
 */
export function getStatusIcon(icon?: string) {
  switch (icon) {
    // Case statuses
    case 'outbound':         return IconLogout;
    case 'lock':             return IconLock;
    case 'fiber_new':        return IconSparkles;
    case 'assignment_ind':   return IconUserCheck;
    case 'pending':          return IconHourglassEmpty;
    case 'task_alt':         return IconCircleCheck;
    case 'pause_circle':     return IconPlayerPause;
    // Registration statuses
    case 'logout':           return IconLogout;
    case 'cancel':           return IconBan;
    case 'hourglass_empty':  return IconHourglassEmpty;
    case 'play_circle':      return IconPlayerPlay;
    default:                 return IconCircleDot;
  }
}

export interface StatusKPICardProps {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  count: number;
  /** Lookup hex colour (or a CSS custom property) — fills the icon chip and marks the active card. */
  color: string;
  active: boolean;
  onClick: () => void;
}

export function StatusKPICard({ icon: Icon, label, count, color, active, onClick }: StatusKPICardProps) {
  return (
    <UnstyledButton onClick={onClick} style={{ display: 'block', width: '100%' }}>
      <Card
        withBorder
        radius="md"
        padding="md"
        style={{
          // A 2px border rather than a wash carries the selection: on the dark theme a 10%
          // tint of a dark status colour is indistinguishable from the card surface.
          borderColor: active ? color : undefined,
          borderWidth: active ? 2 : 1,
          backgroundColor: active ? brandTint(color, 0.12) : undefined,
          transition: 'background-color 120ms ease, border-color 120ms ease',
        }}
      >
        <Group gap="sm" wrap="nowrap">
          {/*
            Filled, not tinted. The old `{ background: `${c}22`, color: c }` put the glyph in the raw
            status colour over a 13% wash of the same hue — unreadable when a dark status colour lands
            on the dark theme, and outright broken when `color` is a CSS variable (`var(…)22` is not a
            colour, so the background silently vanished).
          */}
          <ThemeIcon size={40} radius="md" variant="filled" style={brandSolidStyle(color)}>
            <Icon size={20} />
          </ThemeIcon>
          <div style={{ minWidth: 0 }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed" truncate>{label}</Text>
            <Text size="xl" fw={700}>{count}</Text>
          </div>
        </Group>
      </Card>
    </UnstyledButton>
  );
}

export function StatusKPISkeleton() {
  return (
    <Card withBorder radius="md" padding="md">
      <Group gap="sm" wrap="nowrap">
        <Skeleton height={40} width={40} radius="md" />
        <div style={{ flex: 1 }}>
          <Skeleton height={10} width="60%" radius="sm" mb={8} />
          <Skeleton height={20} width="40%" radius="sm" />
        </div>
      </Group>
    </Card>
  );
}

export default StatusKPICard;
